import type React from 'react';
import {Box, Checkbox, FormControlLabel, FormGroup, InputAdornment, Link, MenuItem, Stack, Switch, TextField, Typography} from '@mui/material';
import {Tag} from '~/components/tag/Tag';
import type {Setting, SettingValue} from '../types/settingsTypes';

type Props = {
    setting: Setting;
    value: SettingValue;
    error?: string;
    onChange: (value: SettingValue) => void;
    onReset: () => void;
};

const show = (v: SettingValue, s: Setting) => {
    if (Array.isArray(v)) return v.map(x => s.choices.find(c => c.value === x)?.label ?? x).join(', ');
    if (typeof v === 'boolean') return v ? 'On' : 'Off';
    return s.choices.find(c => String(c.value) === String(v))?.label ?? `${v}${s.unit && s.unit !== 'P' ? ` ${s.unit}` : ''}`;
};

/** One setting: label and help, the input for its kind, and "Reset to default" when it differs from the default. */
export const SettingField: React.FC<Props> = ({setting: s, value, error, onChange, onReset}) => {
    const differs = JSON.stringify(value) !== JSON.stringify(s.default);
    const id = `setting-${s.key}`;
    let input: React.ReactNode;
    if (s.kind === 'boolean') {
        input = <Switch id={id} checked={Boolean(value)} onChange={e => onChange(e.target.checked)}/>;
    } else if (s.kind === 'multi') {
        const picked = value as string[];
        input = (
            <FormGroup row>
                {s.choices.map(c => (
                    <FormControlLabel key={c.value} label={c.label} control={
                        <Checkbox size="small" checked={picked.includes(String(c.value))}
                                  onChange={e => onChange(e.target.checked ? [...picked, String(c.value)]
                                      : picked.filter(x => x !== String(c.value)))}/>
                    }/>
                ))}
            </FormGroup>
        );
    } else if (s.kind === 'choice') {
        input = (
            <TextField id={id} select size="small" value={String(value)} sx={{minWidth: 200}} error={Boolean(error)}
                       onChange={e => onChange(typeof s.default === 'number' ? Number(e.target.value) : e.target.value)}>
                {s.choices.map(c => <MenuItem key={c.value} value={String(c.value)}>{c.label}</MenuItem>)}
            </TextField>
        );
    } else {
        const numeric = s.kind !== 'text';
        input = (
            <TextField id={id} size="small" type={numeric ? 'number' : 'text'} value={value as string | number}
                       error={Boolean(error)} sx={{width: numeric ? 180 : 260}}
                       onChange={e => onChange(numeric ? (e.target.value === '' ? '' : Number(e.target.value)) : e.target.value)}
                       slotProps={{
                           htmlInput: {min: s.min ?? undefined, max: s.max ?? undefined, step: s.kind === 'integer' ? 1 : 'any'},
                           input: s.unit ? {endAdornment: <InputAdornment position="end">{s.unit}</InputAdornment>} : undefined,
                       }}/>
        );
    }

    return (
        <Stack direction={{xs: 'column', md: 'row'}} gap={{xs: 1, md: 3}} sx={{py: 1.5}}>
            <Box sx={{flex: {md: '0 0 340px'}, minWidth: 0}}>
                <Stack direction="row" gap={1} alignItems="center" flexWrap="wrap">
                    <Typography component="label" htmlFor={id} variant="body2" sx={{fontWeight: 600}}>{s.label}</Typography>
                    {s.rebuild && <Tag label="After rebuild" color="warning" solid={false} title="Takes effect at the next facts and benchmark build"/>}
                </Stack>
                <Typography variant="caption" color="text.secondary" sx={{display: 'block'}}>{s.help}</Typography>
            </Box>
            <Box sx={{flex: 1, minWidth: 0}}>
                {input}
                {error && <Typography variant="caption" color="error" sx={{display: 'block'}}>{error}</Typography>}
                <Typography variant="caption" color="text.secondary" sx={{display: 'block', mt: 0.5}}>
                    {`Default: ${show(s.default, s)}`}
                    {differs && <> · <Link component="button" variant="caption" onClick={onReset}>Reset to default</Link></>}
                </Typography>
            </Box>
        </Stack>
    );
};
